<template>
    <div v-if="ticket" class="checkout-activity-ticket">
        <Element font="font-label-l-bold" class="checkout-activity-ticket-title">
            {{ ticketTitle }}
        </Element>

        <div class="checkout-activity-ticket-name">
            <TruncateText v-if="ticket.Description" :text="ticket.Description" :rows="1" />
        </div>

        <div class="checkout-activity-ticket-fields">
            <div class="checkout-activity-ticket-fields-genders">
                <div class="checkout-activity-ticket-fields-genders-label">
                    {{ t({ key: 'shared.gender' }) }}
                </div>
                <RadioField
                    v-model="ticket.Pax.Gender"
                    :name="genderFieldName"
                    :input="{ items: genderItems, inline: true }" />
            </div>
            <TextboxField
                v-model="ticket.Pax.FirstName"
                :name="firstNameFieldName"
                :input="{
                    label: t({ key: 'checkout.first_name_placeholder_label' }),
                    capitalize: true,
                    autocomplete: 'given-name',
                }" />
            <TextboxField
                v-model="ticket.Pax.LastName"
                :name="lastNameFieldName"
                :input="{
                    label: t({ key: 'checkout.last_name_placeholder_label' }),
                    capitalize: true,
                    autocomplete: 'family-name',
                }" />
            <DateInputField
                v-if="isMandatoryField(MandatoryFieldId.DATE_OF_BIRTH)"
                v-model="ticket.Pax.DateOfBirth"
                :name="dateOfBirthFieldName"
                :input="{
                    label: t({ key: 'shared.date_of_birth' }),
                    hint: getDateHint(),
                    autocomplete: 'bday',
                }" />
            <TextboxField
                v-if="isMandatoryField(MandatoryFieldId.EMAIL)"
                v-model="ticket.Pax.Email"
                :name="emailFieldName"
                :input="{
                    label: t({ key: 'checkout.email_label' }),
                    autocomplete: 'email',
                }" />
            <PhoneField
                v-if="isMandatoryField(MandatoryFieldId.PHONE_NUMBER)"
                v-model="ticket.Pax.Phone"
                :name="phoneFieldName"
                :input="{
                    countryCode,
                    label: t({ key: 'shared.phone_number' }),
                }" />
            <TextboxField
                v-if="isMandatoryField(MandatoryFieldId.ADDRESS)"
                v-model="ticket.Pax.Address"
                :name="addressFieldName"
                :input="{
                    label: t({ key: 'shared.address' }),
                    autocomplete: 'street-address',
                }" />
            <TextboxField
                v-if="isMandatoryField(MandatoryFieldId.PERSONAL_ID_NUMBER)"
                v-model="ticket.Pax.PersonalIdNumber"
                :name="personalIdNumberFieldName"
                :input="{ label: t({ key: 'checkout.personal_id_number' }) }" />
            <SelectField
                v-if="isMandatoryField(MandatoryFieldId.NATIONALITY)"
                v-model="selectedNationality"
                :name="nationalityFieldName"
                :input="{
                    label: t({ key: 'shared.nationality' }),
                    items: nationalitySelectItems,
                }" />
            <TextboxField
                v-if="isMandatoryField(MandatoryFieldId.ORGANIZATION)"
                v-model="ticket.Pax.Organization"
                :name="organizationFieldName"
                :input="{
                    label: t({ key: 'shared.organization' }),
                    autocomplete: 'organization',
                }" />
            <TextboxField
                v-if="isMandatoryField(MandatoryFieldId.PASSPORT_ID)"
                v-model="ticket.Pax.PassportNumber"
                :name="passportNumberFieldName"
                :input="{ label: t({ key: 'shared.passport_number' }) }" />
            <DateInputField
                v-if="isMandatoryField(MandatoryFieldId.PASSPORT_EXPIRY)"
                v-model="ticket.Pax.PassportExpirationDate"
                :name="passportExpirationFieldName"
                :input="{
                    label: t({ key: 'shared.passport_expiration_date' }),
                    hint: getDateHint(),
                    tooltip: t({
                        key: 'shared.passport_expiration_date_disclaimer',
                    }),
                }" />
            <TextboxField
                v-if="isMandatoryField(MandatoryFieldId.LANGUAGE)"
                v-model="ticket.Pax.Language"
                :name="languageFieldName"
                :input="{
                    label: t({ key: 'shared.language' }),
                    autocomplete: 'language',
                }" />
            <SpecificTicketField
                v-for="field in ticket.Pax.TicketFields"
                :key="field.Id"
                :field="field"
                :index="index"
                @addCustomField="$emit('addCustomField', $event)"
                @removeCustomField="$emit('removeCustomField', $event)" />
        </div>
    </div>
</template>
<script lang="ts">
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import {
        ActivityProduct,
        ActivityTicket,
        Customer,
        Gender,
        MandatoryFieldId,
        Nationality,
    } from '@/api/Cart/cart.interfaces';
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { Market } from '@shared/config/market/market.models';
    import { isMandatory } from '@shared/helpers/product';
    import {
        formatTicketFieldName,
        getDateHint,
    } from '@/components/checkoutPageV3/Sections/Products/Activity/Activity.helpers';
    import { capitalizeWords } from '@shared/utils/string';
    import Element from '@ui/components/Element/Element.vue';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import RadioField from '@ui/components/FormV2/Fields/Radio/RadioField.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import PhoneField from '@ui/components/FormV2/Fields/Phone/PhoneField.vue';
    import SelectField from '@ui/components/FormV2/Fields/Select/SelectField.vue';
    import TruncateText from '@ui/components/TruncateText/TruncateText.vue';
    import SpecificTicketField from '../../../Ferry/Form/SpecificTicketField.vue';

    export default defineComponent({
        name: 'Ticket',
        methods: {
            getDateHint,
            t,
        },
        components: {
            Element,
            SpecificTicketField,
            SelectField,
            PhoneField,
            DateInputField,
            RadioField,
            TextboxField,
            TruncateText,
        },
        emits: ['addCustomField', 'removeCustomField'],
        props: {
            productId: {
                type: String,
                required: true,
            },
            ticket: {
                type: Object as PropType<ActivityTicket>,
                required: true,
            },
            index: {
                type: Number,
                required: true,
            },
            activity: {
                type: Object as PropType<ActivityProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();

            const selectedNationality = ref<string | null>(
                props.ticket.Pax.Nationality?.NationalityName ?? null
            );

            const customer = computed<Customer>(() => store.getters['cart/getCustomer']);
            const market = computed((): Market => store.getters['general/getMarket']);
            const nationalities = computed<Nationality[]>(
                () => store.getters['content/getCheckoutNationalities']
            );

            const nationalitySelectItems = computed(() => {
                return nationalities.value.map((item) => ({
                    value: item.NationalityName,
                    label: item.NationalityName,
                }));
            });

            const genderItems = computed(() => {
                const keys = Object.keys(Gender);
                return keys.map((key) => ({
                    value: key,
                    label: t({ key: 'shared.genders.' + key.toLowerCase() }),
                }));
            });

            const countryCode = computed((): string | undefined => {
                const customerCountryCode =
                    customer.value.CountryCode &&
                    customer.value.CountryCode !== '' &&
                    customer.value.CountryCode !== '0';

                if (customerCountryCode) {
                    return customer.value.CountryCode;
                }

                if (market.value) {
                    return market.value?.fullLocale.split('-')[1];
                }

                return undefined;
            });

            const firstNameFieldName = computed(() =>
                formatTicketFieldName(props.index, 'firstName')
            );
            const lastNameFieldName = computed(() =>
                formatTicketFieldName(props.index, 'lastName')
            );
            const personalIdNumberFieldName = computed(() =>
                formatTicketFieldName(props.index, 'personalIdNumber')
            );
            const emailFieldName = computed(() =>
                formatTicketFieldName(props.index, 'email')
            );
            const phoneFieldName = computed(() =>
                formatTicketFieldName(props.index, 'phone')
            );
            const nationalityFieldName = computed(() =>
                formatTicketFieldName(props.index, 'nationality')
            );
            const genderFieldName = computed(() =>
                formatTicketFieldName(props.index, 'gender')
            );
            const organizationFieldName = computed(() =>
                formatTicketFieldName(props.index, 'organization')
            );
            const passportNumberFieldName = computed(() =>
                formatTicketFieldName(props.index, 'passportNumber')
            );
            const passportExpirationFieldName = computed(() =>
                formatTicketFieldName(props.index, 'passportExpirationDate')
            );
            const addressFieldName = computed(() =>
                formatTicketFieldName(props.index, 'address')
            );
            const dateOfBirthFieldName = computed(() =>
                formatTicketFieldName(props.index, 'dateOfBirth')
            );
            const languageFieldName = computed(() =>
                formatTicketFieldName(props.index, 'language')
            );
            // check this piece ticket title no background

            const isMandatoryField = (field: string) => {
                const fields = props.ticket.Pax.MandatoryFields;
                return isMandatory(fields, field);
            };

            const ticketTitle = computed(() => capitalizeWords(t({ key: 'shared.ticket' })));

            watch(
                () => selectedNationality.value,
                (newValue, oldValue) => {
                    if (newValue && newValue !== oldValue) {
                        const newNationality = nationalities.value.find(
                            (n) => n.NationalityName === newValue
                        );
                        if (newNationality) props.ticket.Pax.Nationality = newNationality;
                    }
                }
            );

            return {
                countryCode,
                nationalitySelectItems,
                selectedNationality,
                genderItems,
                genderFieldName,
                firstNameFieldName,
                lastNameFieldName,
                personalIdNumberFieldName,
                emailFieldName,
                phoneFieldName,
                nationalityFieldName,
                organizationFieldName,
                passportNumberFieldName,
                passportExpirationFieldName,
                addressFieldName,
                dateOfBirthFieldName,
                languageFieldName,
                MandatoryFieldId,
                ticketTitle,
                isMandatoryField,
            };
        },
    });
</script>
<style lang="scss" scoped>
    @forward './Ticket';
</style>
